'use client'

import React from 'react'

import { useExtractSpaceProps } from '../../hooks'
import { SpaceProps } from '../../themes'
import { Field } from '../Field'
import { ButtonRadio } from './ButtonRadio'
import { ButtonRadioContainer } from './styles'

export interface ButtonRadioFieldProps<
  T extends number | string,
  Multiple extends boolean,
> extends SpaceProps {
  /**
   * ID Attribute
   */
  id?: string

  /**
   * Class name
   */
  className?: string

  /**
   * Field label
   */
  label?: string

  /**
   * Required flag
   */
  required?: boolean

  /**
   * Icon to display before the error message
   */
  errorIcon?: React.ReactNode

  /**
   * Error messages
   */
  errorMessages?: string | string[]

  /**
   * Icon to display before the caption message
   */
  captionIcon?: React.ReactNode

  /**
   * Caption
   */
  captionMessages?: string | string[]

  /**
   * Options
   */
  options: {
    label: string
    value: T
  }[]

  /**
   * Selected value (for single mode)
   */
  value?: Multiple extends true ? never : T

  /**
   * Selected values (for multiple mode)
   */
  values?: Multiple extends true ? T[] : never

  /**
   * Multiple selection mode
   */
  multiple: Multiple

  /**
   * onChange handler
   */
  onChange: Multiple extends true
    ? (values: T[]) => void
    : Multiple extends false | undefined
    ? (value: T) => void
    : never
}

export const ButtonRadioField = <T extends number | string, V extends boolean>({
  id,
  className,
  label,
  required,
  errorIcon,
  errorMessages,
  captionIcon,
  captionMessages,
  options,
  value,
  values,
  multiple,
  onChange,
  ...props
}: ButtonRadioFieldProps<T, V>) => {
  const autoId = React.useId()
  const inputId = id ?? autoId
  const [spaceProps] = useExtractSpaceProps(props)

  return (
    <Field className={className} {...spaceProps}>
      {label && (
        <Field.Label id={inputId} required={required}>
          {label}
        </Field.Label>
      )}

      <Field.Content mt={label ? 2 : 0}>
        <ButtonRadioContainer>
          {options.map((option) => (
            <ButtonRadio
              key={option.value}
              selected={
                multiple
                  ? values && values.includes(option.value)
                  : value === option.value
              }
              onClick={() => {
                if (multiple) {
                  ;(onChange as (values: T[]) => void)(
                    values && values.includes(option.value)
                      ? values.filter((value) => value !== option.value)
                      : [...(values ?? []), option.value],
                  )
                } else {
                  ;(onChange as (value: T) => void)(option.value as T)
                }
              }}
            >
              {option.label}
            </ButtonRadio>
          ))}
        </ButtonRadioContainer>
      </Field.Content>

      <Field.Messages
        mt={2}
        type="error"
        messages={errorMessages}
        icon={errorIcon}
      />

      <Field.Messages
        mt={errorMessages || (errorMessages && errorMessages.length) ? 1 : 2}
        type="caption"
        messages={captionMessages}
        icon={captionIcon}
      />
    </Field>
  )
}
